import { App, Plugin, watch, defineAsyncComponent, type Component, markRaw } from 'vue';
import { useAppOptionsStore } from './stores/app-options';
import { Vue3ProgressPlugin } from '@marcoschulte/vue3-progress';
import JsonViewer from 'vue3-json-viewer';
import 'vue3-json-viewer/dist/vue3-json-viewer.css';
import * as Sentry from '@sentry/vue';

import type { User } from '@lib/types';

import { AppMapPlugin } from '@lib/map/plugin';

export interface EfLibOptions {
	search?: {
		callback?: (s: string) => boolean;
		component?: Component;
		displayBreakpoint?: string;
	};
	//
	plugins: {};
	//
	layout: {
		context_header_slot?: Component;
	};
}

const plugin: Plugin = {
	install: (app: App, options?: EfLibOptions) => {
		const auth = app.config.globalProperties.$auth;
		const appOptions = useAppOptionsStore();
		if (options?.search) {
			appOptions.search = {
				callback: options.search.callback,
				component: options.search.component ? markRaw(options.search.component) : undefined,
				displayBreakpoint: options.search.displayBreakpoint,
			};
		}
		//
		const context_header_slot = options?.layout?.context_header_slot ?? null;
		if (context_header_slot) {
			// If slot is a function returning a Promise (lazy import)
			if (typeof context_header_slot === 'function' && context_header_slot.prototype === undefined) {
				appOptions.contextHeaderSlot = markRaw(
					defineAsyncComponent(context_header_slot as () => Promise<Component>),
				);
			} else {
				// Normal component
				appOptions.contextHeaderSlot = markRaw(context_header_slot as Component);
			}
		} else {
			appOptions.contextHeaderSlot = null;
		}

		// Use some plugins we need later
		app.use(Vue3ProgressPlugin);
		app.use(JsonViewer);

		// Add sentry.io
		if (!!window.env?.sentry_dsn) {
			Sentry.init({
				app,
				dsn: window.env.sentry_dsn,
				// Setting this option to true will send default PII data to Sentry.
				// For example, automatic IP address collection on events
				sendDefaultPii: true,
			});
		}

		// Add Map Plugin
		app.use(AppMapPlugin, {});

		// Action on Login/Logout and user change
		watch(
			() => auth.user(),
			async (user: User) => {
				// User logged in
				if (user) {
					// Setup Sentry user information
					Sentry.setUser({ id: user.id, email: user.email });
				}
				// User logged out
				else {
					// Remove Sentry user Info
					Sentry.setUser(null);
				}
			},
		);
	},
};

export default plugin;
